import { fetchApi } from "@/lib/api/index.ts";
import { mentorStoriesKey } from "@/lib/hooks/mentor-stories/use-mentor-stories";
import {
  isStoryKey,
  storyColor,
  type StoryComment,
  type StoryDraft,
  type StorySection,
} from "@/lib/mentor-stories/story-content";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export const STORY_REVIEW_QUERY_KEY = ["mentor-story-review", "me"] as const;
export const STORY_REVIEW_QUEUE_KEY = ["mentor-story-review", "queue"] as const;

export type StoryReviewStatus =
  | "in_progress"
  | "in_review"
  | "changes_requested"
  | "approved";

export interface StoryReview {
  id: string;
  userId: string;
  status: StoryReviewStatus;
  draft: StoryDraft;
  published: StoryDraft | null;
  comments: StoryComment[];
  navUnlocked: boolean;
  submittedAt: string | null;
  updatedAt: string;
}

const asRecord = (value: unknown): Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const asSection = (value: unknown): StorySection => {
  const row = asRecord(value);
  const photos = Array.isArray(row.photos)
    ? row.photos.flatMap((photo) => {
        const item = asRecord(photo);
        if (typeof item.slot !== "string" || typeof item.key !== "string") {
          return [];
        }
        return [
          {
            key: item.key,
            slot: item.slot,
            url: typeof item.url === "string" ? item.url : "",
          },
        ];
      })
    : [];
  return {
    caption: typeof row.caption === "string" ? row.caption : null,
    color: storyColor(typeof row.color === "string" ? row.color : null)?.id ?? null,
    photos,
  };
};

const asDraft = (value: unknown): StoryDraft => {
  const row = asRecord(value);
  return {
    more_about_me: asSection(row.more_about_me),
    my_faves: asSection(row.my_faves),
    my_vibe: asSection(row.my_vibe),
    slides: Array.isArray(row.slides)
      ? row.slides.flatMap((slide) => {
          const item = asRecord(slide);
          if (typeof item.key !== "string" || typeof item.url !== "string") {
            return [];
          }
          return [
            {
              eyebrow: typeof item.eyebrow === "string" ? item.eyebrow : null,
              header: typeof item.header === "string" ? item.header : null,
              key: item.key,
              order: typeof item.order === "number" ? item.order : 0,
              url: item.url,
            },
          ];
        })
      : [],
  };
};

const asComments = (value: unknown): StoryComment[] => {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    const row = asRecord(item);
    if (
      typeof row.section !== "string" ||
      !isStoryKey(row.section) ||
      typeof row.note !== "string"
    ) {
      return [];
    }
    return [
      {
        note: row.note,
        ...(Array.isArray(row.photo_keys)
          ? {
              photo_keys: row.photo_keys.filter(
                (key): key is string => typeof key === "string",
              ),
            }
          : {}),
        section: row.section,
        status: row.status === "resolved" ? "resolved" : "open",
      },
    ];
  });
};

const parseReview = (value: unknown): StoryReview | null => {
  const row = asRecord(value);
  if (typeof row.id !== "string" || typeof row.user_id !== "string") return null;
  const status = row.status;
  if (
    status !== "in_progress" &&
    status !== "in_review" &&
    status !== "changes_requested" &&
    status !== "approved"
  ) {
    return null;
  }
  const metadata = asRecord(row.metadata);
  return {
    comments: asComments(row.comments),
    draft: asDraft(row.draft),
    id: row.id,
    navUnlocked: metadata.nav_unlocked === true,
    published: row.published ? asDraft(row.published) : null,
    status,
    submittedAt: typeof metadata.submitted_at === "string" ? metadata.submitted_at : null,
    updatedAt: typeof row.updated_at === "string" ? row.updated_at : "",
    userId: row.user_id,
  };
};

const loadMyStoryReview = async (): Promise<StoryReview | null> => {
  const response = await fetchApi<unknown>({
    method: "GET",
    params: { type: "story" },
    url: "/v1/reviews/me",
  });
  return parseReview(response.data);
};

export const useMyStoryReview = (enabled = true) =>
  useQuery({
    enabled,
    queryFn: loadMyStoryReview,
    queryKey: STORY_REVIEW_QUERY_KEY,
  });

export const useSaveStoryDraft = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (draft: StoryDraft) => {
      const response = await fetchApi<unknown>({
        data: { draft, type: "story" },
        method: "PATCH",
        url: "/v1/reviews/me",
      });
      const review = parseReview(response.data);
      if (!review) throw new Error("Story draft was not saved");
      return review;
    },
    onSuccess: (review) => {
      queryClient.setQueryData(STORY_REVIEW_QUERY_KEY, review);
    },
  });
};

export const useSubmitStoryReview = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const response = await fetchApi<unknown>({
        data: { type: "story" },
        method: "POST",
        url: "/v1/reviews/me/submit",
      });
      const review = parseReview(response.data);
      if (!review) throw new Error("Story was not submitted");
      return review;
    },
    onSuccess: (review) => {
      queryClient.setQueryData(STORY_REVIEW_QUERY_KEY, review);
    },
  });
};

export const useRemovePublishedStory = (mentorId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await fetchApi({
        method: "DELETE",
        params: { type: "story" },
        url: `/v1/reviews/users/${mentorId}`,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: mentorStoriesKey(mentorId) });
      void queryClient.invalidateQueries({ queryKey: STORY_REVIEW_QUEUE_KEY });
    },
  });
};

export const useDeleteStoryDraft = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const response = await fetchApi<unknown>({
        method: "DELETE",
        params: { type: "story" },
        url: "/v1/reviews/me",
      });
      return parseReview(response.data);
    },
    onSuccess: (review) => {
      queryClient.setQueryData(STORY_REVIEW_QUERY_KEY, review);
    },
  });
};

export const useStoryReviewQueue = (enabled = true) =>
  useQuery({
    enabled,
    queryFn: async () => {
      const response = await fetchApi<{ reviews: unknown[] }>({
        method: "GET",
        params: { type: "story" },
        url: "/v1/reviews",
      });
      return (response.data.reviews ?? []).flatMap((row) => {
        const review = parseReview(row);
        return review ? [review] : [];
      });
    },
    queryKey: STORY_REVIEW_QUEUE_KEY,
  });

export const useStoryReview = (reviewId: string, enabled = true) =>
  useQuery({
    enabled: enabled && reviewId.length > 0,
    queryFn: async () => {
      const response = await fetchApi<unknown>({
        method: "GET",
        url: `/v1/reviews/${reviewId}`,
      });
      const review = parseReview(response.data);
      if (!review) throw new Error("Story review was not found");
      return review;
    },
    queryKey: [...STORY_REVIEW_QUEUE_KEY, reviewId],
  });

export const useApproveStoryReview = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (reviewId: string) => {
      const response = await fetchApi<unknown>({
        method: "POST",
        url: `/v1/reviews/${reviewId}/approve`,
      });
      const review = parseReview(response.data);
      if (!review) throw new Error("Story was not approved");
      return review;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: STORY_REVIEW_QUEUE_KEY });
    },
  });
};

export const useRequestStoryChanges = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      reviewId: string;
      comments: Array<{ section: string; note: string }>;
    }) => {
      const response = await fetchApi<unknown>({
        data: { comments: input.comments },
        method: "POST",
        url: `/v1/reviews/${input.reviewId}/request-changes`,
      });
      const review = parseReview(response.data);
      if (!review) throw new Error("Comments were not sent");
      return review;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: STORY_REVIEW_QUEUE_KEY });
    },
  });
};
